<template>
  <div class="simulator-panel">
    <section class="panel-header">
      <div>
        <h3>Ford 模拟诊断请求</h3>
        <p>每种请求已预填一组可执行样例，可直接发送，也可修改任意参数</p>
      </div>
      <el-tag effect="plain" type="warning">测试工具</el-tag>
    </section>

    <el-alert
      title="测试入口使用网站登录权限并直接调用业务服务；正式客户端还会经过 SM2 签名、诊断授权和风控校验，因此授权类返回可能不同。"
      type="warning"
      :closable="false"
      show-icon
    />

    <div class="simulator-layout">
      <section class="config-section">
        <div class="section-heading">
          <div>
            <h4>请求配置</h4>
            <p>{{ engineLabel }} · 功能类型 {{ currentTemplate.funcType }}</p>
          </div>
          <el-tag effect="plain">{{ currentTemplate.funcPath }}</el-tag>
        </div>

        <el-form label-position="top" class="sim-form">
          <el-form-item label="请求服务器">
            <el-radio-group v-model="form.serverKey" class="choice-group">
              <el-radio-button v-for="server in servers" :key="server.key" :label="server.key">
                {{ server.label }}
              </el-radio-button>
            </el-radio-group>
          </el-form-item>

          <el-form-item label="工具类型">
            <el-radio-group v-model="form.engine" class="choice-group">
              <el-radio-button label="new">新工具</el-radio-button>
              <el-radio-button label="legacy" :disabled="!currentTemplate.legacySupported">旧工具</el-radio-button>
            </el-radio-group>
            <div v-if="!currentTemplate.legacySupported" class="field-tip">该功能没有旧工具实现，只能使用新工具测试。</div>
          </el-form-item>

          <el-form-item label="接口类型">
            <el-select v-model="form.templateKey" filterable class="full-control">
              <el-option v-for="template in templates" :key="template.key" :label="template.title" :value="template.key" />
            </el-select>
          </el-form-item>

          <div class="field-grid">
            <el-form-item label="序列号">
              <el-input v-model.trim="form.sn" clearable />
            </el-form-item>
            <el-form-item label="VIN">
              <el-input v-model.trim="form.vin" clearable />
            </el-form-item>
            <el-form-item label="软件版本">
              <el-input v-model.trim="form.diagVersion" clearable />
            </el-form-item>
            <el-form-item label="软件包 ID">
              <el-input v-model.trim="form.diagId" clearable />
            </el-form-item>
            <el-form-item label="语言">
              <el-select v-model="form.language" class="full-control">
                <el-option label="简体中文" value="CN" />
                <el-option label="英文" value="EN" />
                <el-option label="繁体中文" value="HK" />
              </el-select>
            </el-form-item>
          </div>
        </el-form>
      </section>

      <section class="config-section parameter-section">
        <div class="section-heading">
          <div>
            <h4>功能参数</h4>
            <p>已按正式请求字段预填，可直接修改</p>
          </div>
          <div class="heading-actions">
            <el-button
              :icon="Select"
              type="primary"
              plain
              size="small"
              :loading="savingDefaults"
              :disabled="!parameterFields.length || loadingDefaults"
              @click="saveCurrentDefaults"
            >
              更新默认参数
            </el-button>
            <el-button :icon="Refresh" plain size="small" :disabled="loadingDefaults" @click="resetCurrentTemplate">
              恢复样例
            </el-button>
          </div>
        </div>

        <el-form v-if="parameterFields.length" label-position="top" class="parameter-grid">
          <el-form-item v-for="field in parameterFields" :key="field.path" :label="field.label">
            <el-input-number
              v-if="field.input === 'number'"
              :model-value="getParameterValue(parm, field.path)"
              controls-position="right"
              class="full-control"
              @update:model-value="setParameterValue(parm, field.path, $event)"
            />
            <el-input
              v-else
              :model-value="getParameterValue(parm, field.path)"
              :type="field.input === 'textarea' ? 'textarea' : 'text'"
              :rows="field.input === 'textarea' ? 4 : undefined"
              clearable
              @update:model-value="setParameterValue(parm, field.path, $event)"
            />
            <div v-if="field.label !== field.path" class="field-path">字段：{{ field.path }}</div>
          </el-form-item>
        </el-form>
        <el-empty v-else description="此请求不需要额外功能参数" :image-size="72" />

        <div class="request-target">
          <span>正式接口</span>
          <strong>{{ formalEndpoint }}</strong>
        </div>

        <div class="form-actions">
          <el-button plain @click="clearResult">清空结果</el-button>
          <el-button type="success" :icon="Promotion" :loading="sending" @click="sendSimulatorRequest">
            一键发送测试
          </el-button>
        </div>
      </section>
    </div>

    <section class="config-section result-section">
      <div class="section-heading">
        <div>
          <h4>请求结果</h4>
          <p>按正式接口的状态码、消息和业务字段展示</p>
        </div>
        <el-tag v-if="resultEnvelope" :type="resultTagType" effect="plain">{{ resultStatusText }}</el-tag>
      </div>

      <el-empty v-if="!resultEnvelope && !sending" description="点击“一键发送测试”查看结果" :image-size="76" />
      <el-skeleton v-else-if="sending" :rows="4" animated />
      <template v-else>
        <el-descriptions :column="3" border class="result-summary">
          <el-descriptions-item label="状态码">{{ resultEnvelope.code }}</el-descriptions-item>
          <el-descriptions-item label="消息">{{ resultEnvelope.message }}</el-descriptions-item>
          <el-descriptions-item label="耗时">{{ executionDuration }}</el-descriptions-item>
        </el-descriptions>

        <el-table v-if="resultRows.length" :data="resultRows" border size="small" class="result-table">
          <el-table-column prop="label" label="结果字段" min-width="220" />
          <el-table-column prop="value" label="结果值" min-width="360">
            <template #default="{ row }"><span class="result-value">{{ row.value }}</span></template>
          </el-table-column>
        </el-table>
        <el-empty v-else description="请求已返回，无业务数据" :image-size="64" />
      </template>
    </section>
  </div>
</template>

<script setup>
import { computed, onActivated, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { Promotion, Refresh, Select } from '@element-plus/icons-vue';

import {
  getFordDiagnosticSimulatorTemplates,
  runFordDiagnosticSimulator,
  saveFordDiagnosticSimulatorTemplate,
} from '@/api/users';
import {
  DEFAULT_SIMULATOR_FORM,
  FORD_SIMULATOR_SERVERS,
  buildSimulatorRunRequest,
  findSimulatorTemplate,
  flattenDisplayRows,
  flattenParameterFields,
  getAllSimulatorTemplates,
  getParameterValue,
  getSimulatorTemplateDefaults,
  mergeServerSimulatorTemplates,
  setParameterValue,
} from '@/utils/fordDiagnosticSimulatorUi.mjs';

const servers = FORD_SIMULATOR_SERVERS;
const templates = ref(getAllSimulatorTemplates());
const initial = getSimulatorTemplateDefaults(DEFAULT_SIMULATOR_FORM.templateKey);
const form = reactive({ ...initial });
const parm = reactive(initial.parm);
const sending = ref(false);
const loadingDefaults = ref(false);
const savingDefaults = ref(false);
const runResult = ref(null);
const serverTemplateCache = new Map();
let templateLoadSequence = 0;

const currentTemplate = computed(() => templates.value.find((item) => item.key === form.templateKey) || findSimulatorTemplate(form.templateKey));
const currentServer = computed(() => servers.find((item) => item.key === form.serverKey) || servers[0]);
const parameterFields = computed(() => flattenParameterFields(parm, currentTemplate.value.fields));
const engineLabel = computed(() => (form.engine === 'legacy' ? '旧工具 exe 请求' : '新工具结构化请求'));
const formalEndpoint = computed(() => (form.engine === 'legacy'
  ? `${currentServer.value.origin}/services/v2/runcmd/tool_GetIVSData.exe`
  : `${currentServer.value.origin}/api/v1/funcEx/${currentTemplate.value.funcPath}`));

const executionData = computed(() => runResult.value?.data || {});
const resultEnvelope = computed(() => {
  if (!runResult.value) return null;
  const execution = executionData.value;
  if (execution.response && typeof execution.response === 'object') return execution.response;
  if (Object.prototype.hasOwnProperty.call(execution, 'stdout')) {
    return { code: runResult.value.code === 200 ? 0 : runResult.value.code, message: runResult.value.msg || '完成', data: { stdout: execution.stdout } };
  }
  if (Object.prototype.hasOwnProperty.call(runResult.value, 'code')) {
    return { code: runResult.value.code, message: runResult.value.message || runResult.value.msg || '请求失败', data: runResult.value.data?.data || null };
  }
  return { code: -1, message: '无法识别服务器返回', data: null };
});
const resultRows = computed(() => flattenDisplayRows(resultEnvelope.value?.data ?? {}));
const executionDuration = computed(() => (executionData.value.durationMs === undefined ? '—' : `${executionData.value.durationMs} ms`));
const resultTagType = computed(() => (Number(resultEnvelope.value?.code) === 0 ? 'success' : 'danger'));
const resultStatusText = computed(() => (Number(resultEnvelope.value?.code) === 0 ? '请求成功' : '请求失败'));

function replaceParm(nextParm) {
  Object.keys(parm).forEach((key) => delete parm[key]);
  Object.assign(parm, JSON.parse(JSON.stringify(nextParm || {})));
}

function applyTemplateDefaults(templateKey, preserveTransport = true) {
  const serverKey = form.serverKey;
  const engine = form.engine;
  const template = templates.value.find((item) => item.key === templateKey);
  const defaults = template
    ? { ...getSimulatorTemplateDefaults(templateKey), ...(template.sample || {}), templateKey: template.key, parm: JSON.parse(JSON.stringify(template.parm || {})) }
    : getSimulatorTemplateDefaults(templateKey);
  Object.assign(form, defaults);
  if (preserveTransport) {
    form.serverKey = serverKey;
    form.engine = template?.legacySupported === false ? 'new' : engine;
  }
  delete form.parm;
  replaceParm(defaults.parm);
  runResult.value = null;
}

function refreshTemplates() {
  templates.value = mergeServerSimulatorTemplates(serverTemplateCache.get(form.serverKey));
  if (!templates.value.some((template) => template.key === form.templateKey)) applyTemplateDefaults(templates.value[0]?.key || DEFAULT_SIMULATOR_FORM.templateKey);
}

function errorMessage(error, fallback) {
  return error?.response?.data?.message || error?.response?.data?.error || error?.info || error?.message || fallback;
}

async function loadServerDefaults(serverKey, applyCurrent = false) {
  const sequence = ++templateLoadSequence;
  loadingDefaults.value = true;
  try {
    const response = await getFordDiagnosticSimulatorTemplates(serverKey);
    const state = response.data?.data || {};
    serverTemplateCache.set(serverKey, state.templates || state.visibleTemplates || []);
    if (sequence !== templateLoadSequence || serverKey !== form.serverKey) return;
    refreshTemplates();
    if (applyCurrent) {
      replaceParm(currentTemplate.value.parm);
      runResult.value = null;
    }
  } catch (error) {
    if (sequence === templateLoadSequence && serverKey === form.serverKey) {
      serverTemplateCache.delete(serverKey);
      refreshTemplates();
      if (applyCurrent) {
        replaceParm(currentTemplate.value.parm);
        runResult.value = null;
      }
      ElMessage.warning(errorMessage(error, '默认参数读取失败，已使用内置样例'));
    }
  } finally {
    if (sequence === templateLoadSequence) loadingDefaults.value = false;
  }
}

async function saveCurrentDefaults() {
  if (savingDefaults.value || !parameterFields.value.length) return;
  const serverKey = form.serverKey;
  const serverLabel = currentServer.value.label;
  const template = currentTemplate.value;
  const storedTemplate = serverTemplateCache.get(serverKey)?.find((item) => item.key === template.key) || template;
  const legacyFuncType = Number(storedTemplate.legacyFuncType ?? storedTemplate.funcType);
  const payload = {
    key: storedTemplate.key,
    title: storedTemplate.title,
    desc: storedTemplate.desc || `funcEx/${storedTemplate.funcPath}`,
    funcPath: storedTemplate.funcPath,
    funcType: Number(storedTemplate.funcType),
    legacySupported: storedTemplate.legacySupported !== false,
    method: storedTemplate.method || 'POST',
    enabled: storedTemplate.enabled !== false,
    parm: JSON.parse(JSON.stringify(parm)),
  };
  if (Number.isFinite(legacyFuncType)) payload.legacyFuncType = legacyFuncType;

  try {
    savingDefaults.value = true;
    const response = await saveFordDiagnosticSimulatorTemplate(serverKey, payload);
    const state = response.data?.data || {};
    serverTemplateCache.set(serverKey, state.templates || state.visibleTemplates || []);
    if (serverKey === form.serverKey) refreshTemplates();
    ElMessage.success(`已更新 ${serverLabel} 的默认参数`);
  } catch (error) {
    ElMessage.warning(errorMessage(error, '默认参数更新失败'));
  } finally {
    savingDefaults.value = false;
  }
}

function resetCurrentTemplate() {
  applyTemplateDefaults(form.templateKey);
  ElMessage.success('已恢复该请求的默认参数');
}

function clearResult() {
  runResult.value = null;
}

async function sendSimulatorRequest() {
  if (sending.value) return;
  try {
    sending.value = true;
    runResult.value = null;
    const request = buildSimulatorRunRequest({ ...form, parm });
    const response = await runFordDiagnosticSimulator(form.serverKey, request);
    runResult.value = response.data;
    const businessCode = Number(runResult.value?.data?.response?.code ?? (runResult.value?.code === 200 ? 0 : runResult.value?.code));
    if (businessCode === 0) ElMessage.success('测试请求已返回');
    else ElMessage.warning(runResult.value?.data?.response?.message || runResult.value?.message || runResult.value?.msg || '请求返回业务错误');
  } catch (error) {
    const message = error?.response?.data?.message || error?.response?.data?.msg || error?.info || error?.message || '发送失败';
    runResult.value = error?.response?.data || { code: -1, message, data: null };
    ElMessage.warning(message);
  } finally {
    sending.value = false;
  }
}

watch(() => form.templateKey, (key) => applyTemplateDefaults(key));
watch(() => form.serverKey, (serverKey) => loadServerDefaults(serverKey, true));
watch(() => form.engine, (engine) => {
  if (engine === 'legacy' && !currentTemplate.value.legacySupported) form.engine = 'new';
  runResult.value = null;
});

onMounted(() => {
  window.addEventListener('ford-simulator-templates-changed', refreshTemplates);
});
onActivated(() => loadServerDefaults(form.serverKey));
onBeforeUnmount(() => window.removeEventListener('ford-simulator-templates-changed', refreshTemplates));
</script>

<style scoped>
.simulator-panel { display: grid; gap: 14px; min-width: 0; }
.panel-header, .config-section { border: 1px solid #dfe8f3; border-radius: 8px; background: #fff; box-shadow: 0 10px 24px rgba(31, 56, 88, .05); }
.panel-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; padding: 16px 18px; }
.panel-header h3, .section-heading h4 { margin: 0; color: #132033; font-weight: 800; }
.panel-header h3 { font-size: 18px; }
.panel-header p, .section-heading p { margin: 5px 0 0; color: #6b7b8f; font-size: 13px; line-height: 1.45; }
.simulator-layout { display: grid; grid-template-columns: minmax(360px, .95fr) minmax(420px, 1.05fr); gap: 14px; align-items: start; }
.config-section { min-width: 0; padding: 16px; }
.section-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 14px; }
.heading-actions { display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap; }
.section-heading h4 { font-size: 16px; }
.sim-form :deep(.el-form-item), .parameter-grid :deep(.el-form-item) { margin-bottom: 14px; }
.choice-group { display: flex; flex-wrap: wrap; gap: 8px; }
.choice-group :deep(.el-radio-button__inner) { min-width: 96px; height: 36px; border: 1px solid #d8e5f2; border-radius: 8px !important; box-shadow: none; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; }
.full-control { width: 100%; }
.field-grid, .parameter-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0 12px; }
.field-tip, .field-path { margin-top: 5px; color: #8a99aa; font-size: 12px; line-height: 1.35; }
.request-target { display: grid; grid-template-columns: 72px minmax(0, 1fr); align-items: center; gap: 10px; min-height: 44px; padding: 0 12px; margin: 4px 0 14px; border: 1px solid #d8e9fb; border-radius: 8px; background: #f2f8ff; }
.request-target span { color: #0f766e; font-size: 12px; font-weight: 800; }
.request-target strong { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #31485f; font-size: 13px; }
.form-actions { display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap; }
.result-summary { margin-bottom: 14px; }
.result-table { width: 100%; }
.result-table :deep(.el-table__header th) { background: #f6f9fc !important; color: #4b5f75; font-weight: 800; }
.result-value { color: #132033; white-space: pre-wrap; word-break: break-word; }
@media (max-width: 1180px) { .simulator-layout { grid-template-columns: 1fr; } }
@media (max-width: 640px) { .panel-header, .section-heading { flex-direction: column; } .field-grid, .parameter-grid { grid-template-columns: 1fr; } .heading-actions, .form-actions { justify-content: flex-start; } }
</style>
